import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { CalendarIcon, HomeIcon, TruckIcon, Loader2, AlertCircle } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { SentryErrorBoundary } from "@/components/SentryErrorBoundary";
import { addFormBreadcrumb, logFormSubmissionError } from "@/lib/formErrorLogger";
import { logError } from "@/lib/sentry";
import { estimateFormSchema, type EstimateFormData } from "@/lib/schemas";
import { supabase } from "@/integrations/supabase/client";
import { createBookingClient } from "@/integrations/supabase/bookingClient";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { 
  isDisposableEmail, 
  getEmailDomain, 
  buildSubmissionMetadata, 
  calculateClientSpamScore,
  shouldBlockSubmission,
  calculateFormTiming
} from "@/lib/spamDetection";
import { formatPhoneNumber } from "@/lib/phoneUtils";
import { useFormAbandonment } from "@/hooks/useFormAbandonment";
import { getOpenAIAdsChoice } from "@/lib/openaiAdsPrivacy";
import { setVisitorBookingContext } from "@/hooks/useVisitorPresence";

import { detectStateFromZip, getAllStates, VALID_ORIGIN_STATES } from "@/lib/zipStateDetection";

// validateServiceArea defined below

const validateServiceArea = (zip: string): { valid: boolean; state: string | null; message?: string } => {
  const state = detectStateFromZip(zip);
  
  if (!state) {
    return {
      valid: false,
      state: null,
      message: "Unable to determine state from ZIP code. Please verify your ZIP code.",
    };
  }
  
  if (!VALID_ORIGIN_STATES.includes(state)) {
    return {
      valid: false,
      state,
      message: `We only provide pickup services from Illinois, Indiana, Wisconsin, and Michigan. Your ZIP code ${zip} is in ${state}.`,
    };
  }
  
  return { valid: true, state };
};

const EstimateFormInner = ({ multiStep = false }: { multiStep?: boolean }) => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const hide = (s: number) => (multiStep && step !== s ? "hidden" : "");
  
  // Anti-spam: Track form load time for timing analysis
  const formLoadedAt = useRef<number>(Date.now());
  
  // Anti-spam: Honeypot field (hidden from users, bots fill it)
  const [honeypot, setHoneypot] = useState("");
  
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    fromZip: "",
    fromState: "",
    toZip: "",
    toState: "",
    moveDate: "",
    homeSize: "",
    notes: "",
    smsConsent: false,
  });

  // Prefill from URL params (?fromZip=&toZip=&moveDate=) for MiniQuoteWidget deep links
  useEffect(() => {
    if (typeof window === "undefined") return;
    const sp = new URLSearchParams(window.location.search);
    const fromZip = (sp.get("fromZip") || "").replace(/\D/g, "").slice(0, 5);
    const toZip = (sp.get("toZip") || "").replace(/\D/g, "").slice(0, 5);
    const moveDate = sp.get("moveDate") || "";
    if (fromZip || toZip || moveDate) {
      setFormData((prev) => ({
        ...prev,
        fromZip: fromZip || prev.fromZip,
        toZip: toZip || prev.toZip,
        moveDate: moveDate || prev.moveDate,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const submissionLockRef = useRef(false);
  const [bypassDuplicateCheck, setBypassDuplicateCheck] = useState(false);
  const [originError, setOriginError] = useState<string | null>(null);
  const [destinationError, setDestinationError] = useState<string | null>(null);
  
  // Cross-table duplicate detection state
  const [duplicateFound, setDuplicateFound] = useState<{
    vbNumber: string;
    source: string;
    createdAt: string;
  } | null>(null);
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);
  const [isCheckingDuplicates, setIsCheckingDuplicates] = useState(false);

  // Check for existing records by email or phone across online_bookings and leads
  // Uses server-side RPC to normalize phone digits for reliable matching
  const checkForExistingRecords = async (email: string, phone: string): Promise<{
    found: boolean;
    vbNumber?: string;
    source?: string;
    createdAt?: string;
  }> => {
    const normalizedPhone = phone.replace(/\D/g, '').slice(-10);
    
    const { data, error } = await supabase
      .rpc('check_duplicate_booking', {
        p_email: email,
        p_phone_digits: normalizedPhone
      });
    
    if (!error && data && data.length > 0) {
      return {
        found: true,
        vbNumber: data[0].vb_number,
        source: data[0].source,
        createdAt: data[0].created_at
      };
    }
    
    return { found: false };
  };

  // Form abandonment tracking (lightweight, no performance impact)
  const { trackFieldChange } = useFormAbandonment(formData, isSubmitted, {
    formName: 'estimate_form',
    pagePath: '/quote',
    totalFields: 11
  });

  // Auto-detect origin state when origin ZIP changes + validate service area
  useEffect(() => {
    if (formData.fromZip.length >= 5) {
      const validation = validateServiceArea(formData.fromZip);
      if (!validation.valid) {
        setOriginError(validation.message || null);
      } else {
        setOriginError(null);
      }

      const detectedState = detectStateFromZip(formData.fromZip);
      if (detectedState && detectedState !== formData.fromState) {
        setFormData(prev => ({ ...prev, fromState: detectedState }));
      }
    } else {
      setOriginError(null);
    }
  }, [formData.fromZip]);

  // Destination: ZIP is authoritative — auto-fill/correct state, never block
  useEffect(() => {
    if (formData.toZip.length < 5) {
      setDestinationError(null);
      return;
    }
    const detected = detectStateFromZip(formData.toZip);
    if (!detected) {
      setDestinationError(null);
      return;
    }
    if (!formData.toState) {
      setFormData(prev => ({ ...prev, toState: detected }));
      setDestinationError(null);
      return;
    }
    if (detected === formData.toState) {
      setDestinationError(null);
      return;
    }
    // Mismatch — auto-correct to ZIP-detected state and show informational note
    setFormData(prev => ({ ...prev, toState: detected }));
    setDestinationError(
      `ZIP ${formData.toZip} is in ${detected}. We've updated the destination state to match.`
    );
  }, [formData.toZip, formData.toState]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Atomic submission lock using ref (survives re-renders, prevents double-clicks)
    if (submissionLockRef.current) return;
    submissionLockRef.current = true;
    
    if (isSubmitting) {
      submissionLockRef.current = false;
      return;
    }
    
    // === PHASE 1 ANTI-SPAM: Client-side checks ===
    const formTimeSeconds = calculateFormTiming(formLoadedAt.current);
    const honeypotTriggered = honeypot.length > 0;
    const disposableEmail = isDisposableEmail(formData.email);
    const clientSpamScore = calculateClientSpamScore(formTimeSeconds, honeypotTriggered, disposableEmail);
    
    console.log("🛡️ Anti-spam check:", { 
      formTimeSeconds, 
      honeypotTriggered, 
      disposableEmail, 
      clientSpamScore 
    });
    
    // Block obvious bots (honeypot triggered or very suspicious)
    if (shouldBlockSubmission(clientSpamScore)) {
      console.warn("🚫 Submission blocked by client-side spam detection");
      toast({
        title: "Submission Error",
        description: "Unable to process your request. Please try again later.",
        variant: "destructive",
      });
      submissionLockRef.current = false;
      return;
    }
    
    // Warn about disposable email but allow submission
    if (disposableEmail) {
      toast({
        title: "Email Warning",
        description: "Please use a permanent email address to receive your quote and booking updates.",
        variant: "destructive",
        duration: 6000,
      });
      submissionLockRef.current = false;
      return;
    }
    
    // Check service area validation before proceeding
    const serviceAreaValidation = validateServiceArea(formData.fromZip);
    if (!serviceAreaValidation.valid) {
      setOriginError(serviceAreaValidation.message || null);
      toast({
        title: "Service Area Restriction",
        description: serviceAreaValidation.message,
        variant: "destructive",
        duration: 8000,
      });
      submissionLockRef.current = false;
      return;
    }
    
    // Explicit state validation before submission (surgical fix)
    if (!formData.fromState || formData.fromState.length !== 2) {
      toast({
        title: "Missing Origin State",
        description: "Please wait for state auto-detection or manually select the origin state",
        variant: "destructive",
      });
      setIsSubmitting(false);
      submissionLockRef.current = false;
      return;
    }
    
    if (!formData.toState || formData.toState.length !== 2) {
      toast({
        title: "Missing Destination State",
        description: "Please wait for state auto-detection or manually select the destination state",
        variant: "destructive",
      });
      setIsSubmitting(false);
      submissionLockRef.current = false;
      return;
    }
    
    // Build submission metadata for server-side spam analysis
    const submissionMetadata = buildSubmissionMetadata(formLoadedAt.current, honeypot);
    
    try {
      setIsSubmitting(true);
      
      // Add breadcrumb
      addFormBreadcrumb("EstimateForm", "submit", {
        fromZip: formData.fromZip,
        toZip: formData.toZip,
        homeSize: formData.homeSize,
      });

      // Validate with zod schema
      const result = estimateFormSchema.safeParse(formData);
      
      if (!result.success) {
        // === DEBUG LOGGING START ===
        console.group("🔴 FORM VALIDATION FAILED");
        console.log("📋 Submitted Form Data:", {
          ...formData,
          email: formData.email ? `${formData.email.substring(0, 3)}***` : '(empty)',
          phone: formData.phone ? `***${formData.phone.slice(-4)}` : '(empty)',
          name: formData.name ? `${formData.name.substring(0, 3)}***` : '(empty)',
        });
        
        console.log("\n🌐 State Auto-Detection Results:");
        console.log("  - Origin ZIP:", formData.fromZip, "→ Detected State:", formData.fromState || "(not detected)");
        console.log("  - Destination ZIP:", formData.toZip, "→ Detected State:", formData.toState || "(not detected)");
        
        console.log("\n❌ Validation Errors:");
        result.error.errors.forEach((err, index) => {
          console.log(`  ${index + 1}. Field: "${err.path[0]}"`, {
            message: err.message,
            code: err.code,
            received: 'received' in err ? err.received : '(no value)',
          });
        });
        
        console.log("\n📊 Summary:");
        console.log("  - Total errors:", result.error.errors.length);
        console.log("  - Failed fields:", result.error.errors.map(e => e.path[0]).join(", "));
        console.groupEnd();
        // === DEBUG LOGGING END ===
        
        // Extract validation errors
        const errors: Record<string, string> = {};
        result.error.errors.forEach((err) => {
          if (err.path[0]) {
            errors[err.path[0] as string] = err.message;
          }
        });
        setValidationErrors(errors);
        
        toast({
          title: "Validation Error",
          description: "Please check the form for errors and try again.",
          variant: "destructive",
        });
        setIsSubmitting(false);
        submissionLockRef.current = false;
        return;
      }

      // Clear validation errors
      setValidationErrors({});

      // === CROSS-TABLE DUPLICATE CHECK ===
      if (!bypassDuplicateCheck) {
        console.log("🔍 Checking for existing records...");
        setIsCheckingDuplicates(true);
        const existingRecord = await checkForExistingRecords(formData.email, formData.phone);
        setIsCheckingDuplicates(false);

        if (existingRecord.found) {
          console.log("⚠️ Duplicate found:", existingRecord.vbNumber);
          setDuplicateFound({
            vbNumber: existingRecord.vbNumber!,
            source: existingRecord.source || 'unknown',
            createdAt: existingRecord.createdAt!
          });
          setShowDuplicateDialog(true);
          setIsSubmitting(false);
          submissionLockRef.current = false;
          return;
        }
        console.log("✅ No duplicates found, proceeding with submission");
      } else {
        console.log("⏭️ Bypassing duplicate check (Start Fresh)");
        setBypassDuplicateCheck(false);
      }

      // Show initial loading toast
      toast({
        title: "Processing Your Request...",
        description: "Generating your VB number and quote",
      });

      // === STEP 1: Generate VB Number ===
      console.group("🔵 STEP 1: VB Number Generation");
      console.log("⏳ Calling generate_vb_number RPC...");
      
      const { data: vbNumber, error: vbError } = await supabase.rpc('generate_vb_number');
      
      if (vbError) {
        console.error("❌ VB number generation FAILED:");
        console.error("  - Message:", vbError.message);
        console.error("  - Code:", vbError.code);
        console.error("  - Details:", vbError.details);
        console.error("  - Hint:", vbError.hint);
        console.groupEnd();
        throw new Error("Failed to generate booking reference number");
      }
      
      console.log("✅ VB Number generated successfully:", vbNumber);
      console.groupEnd();

    // === STEP 2: Insert Booking Record ===
    console.group("🔵 STEP 2: Create Booking Record");
    
    // Calculate move_type with defensive null checks (surgical fix)
    if (!formData.fromState || !formData.toState) {
      throw new Error('Cannot determine move type: missing state information');
    }
    // Block non-IL same-state moves (we only do local moves within Illinois)
    if (formData.fromState === formData.toState && formData.fromState !== 'IL') {
      toast({
        title: "Service Area Restriction",
        description: `We only service local moves within Illinois. For moves within ${formData.fromState}, please contact a local mover in that state. We can help with interstate moves from ${formData.fromState} to other states!`,
        variant: "destructive",
      });
      setIsSubmitting(false);
      submissionLockRef.current = false;
      console.groupEnd();
      return;
    }

    const moveType = (formData.fromState === 'IL' && formData.toState === 'IL') ? 'local' : 'interstate';
    console.log("🚚 Move Type Detected:", moveType, `(${formData.fromState} → ${formData.toState})`);

    // Capture visitor_id + acquisition snapshot BEFORE booking insert so we can stamp them
    // directly onto the booking row. This is the primary CRM source — `traffic_sessions`
    // is now only a fallback / analytics layer.
    let bookingVisitorId: string | null = null;
    let bookingAcquisition: {
      source: string | null;
      medium: string | null;
      campaign: string | null;
      term: string | null;
      entry_page: string | null;
      referrer_domain: string | null;
    } = { source: null, medium: null, campaign: null, term: null, entry_page: null, referrer_domain: null };
    try {
      const { ensureIdentifiers, captureAcquisition } = await import('@/lib/attributionCapture');
      bookingVisitorId = ensureIdentifiers().visitorId;
      const acq = captureAcquisition();
      bookingAcquisition = {
        source: acq.source || null,
        medium: acq.medium || (acq.source === 'referral' ? acq.referrer_domain : null),
        campaign: acq.campaign || null,
        term: acq.term || null,
        entry_page: acq.entry_page || null,
        referrer_domain: acq.referrer_domain || null,
      };
      console.log('[EstimateForm] 🎯 Captured acquisition for CRM:', bookingAcquisition);
    } catch (e) {
      console.warn('[EstimateForm] Could not capture acquisition pre-insert:', e);
    }

    console.log("⏳ Inserting into online_bookings table...");
    console.log("📦 Booking Data (sanitized):", {
      customer_name: formData.name ? `${formData.name.substring(0, 3)}***` : '(empty)',
      customer_email: formData.email ? `${formData.email.substring(0, 3)}***` : '(empty)',
      customer_phone: formData.phone ? `***${formData.phone.slice(-4)}` : '(empty)',
      origin_zip: formData.fromZip,
      origin_state: formData.fromState,
      destination_zip: formData.toZip,
      destination_state: formData.toState,
      move_date: formData.moveDate || null,
      home_size: formData.homeSize,
      move_type: moveType,
      notes: formData.notes ? `${formData.notes.substring(0, 20)}...` : '(none)',
      source: 'website',
      status: 'lead',
      workflow_step: 'quote_requested',
      vb_number: vbNumber,
    });
    
    // Pre-generate the booking id so the request can carry the customer-scoped
    // header (x-booking-id). Without it, reading the row back after insert is
    // refused by the access rules that lock a booking to its own link.
    const newBookingId = crypto.randomUUID();
    const scopedClient = createBookingClient(newBookingId);

    const { data: booking, error: bookingError } = await scopedClient
      .from('online_bookings')
      .insert([{
        id: newBookingId,
        customer_name: formData.name,
        customer_email: formData.email,
        customer_phone: formData.phone,
        origin_zip: formData.fromZip,
        origin_state: formData.fromState,
        destination_zip: formData.toZip,
        destination_state: formData.toState,
        move_date: formData.moveDate || null,
        home_size: formData.homeSize,
        move_type: moveType,
        notes: formData.notes,
        source: 'website',
        status: 'lead',
        workflow_step: 'quote_requested',
        vb_number: vbNumber,
        visitor_id: bookingVisitorId,
        acquisition_source: bookingAcquisition.source,
        acquisition_medium: bookingAcquisition.medium,
        acquisition_campaign: bookingAcquisition.campaign,
        acquisition_term: bookingAcquisition.term,
        acquisition_entry_page: bookingAcquisition.entry_page,
        acquisition_referrer_domain: bookingAcquisition.referrer_domain,
        // Anti-spam metadata for server-side analysis
        spam_score: clientSpamScore,
        submission_metadata: JSON.parse(JSON.stringify({ ...submissionMetadata, openai_ads: await getOpenAIAdsChoice() })),
      }])
      .select()
      .single();

    if (bookingError) {
      console.error("❌ Booking insert FAILED:");
      console.error("  - Message:", bookingError.message);
      console.error("  - Code:", bookingError.code);
      console.error("  - Details:", bookingError.details);
      console.error("  - Hint:", bookingError.hint);
      console.error("  - Status Code:", (bookingError as any).statusCode);
      console.error("\n🔍 Full Error Object:", bookingError);
      console.groupEnd();
      
      // Check for unique constraint violation (duplicate detected at DB level)
      if (bookingError.code === '23505') {
        console.warn("🔒 Database-level duplicate detected — redirecting to resume booking");
        toast({
          title: "Existing Booking Found",
          description: "It looks like you already have a booking with us. Redirecting you to resume it...",
          duration: 5000,
        });
        setIsSubmitting(false);
        submissionLockRef.current = false;
        setTimeout(() => navigate('/resume-booking'), 1500);
        return;
      }
      
      // Send detailed error to Sentry with sanitized form data
      logError(new Error(`Booking insert failed: ${bookingError.message}`), {
        supabaseError: {
          message: bookingError.message,
          code: bookingError.code,
          details: bookingError.details,
          hint: bookingError.hint,
          statusCode: (bookingError as any).statusCode,
        },
        formData: { 
          ...formData, 
          email: '[REDACTED]', 
          phone: '[REDACTED]',
          name: '[REDACTED]'
        },
        vbNumber,
      });
      
      throw new Error("Failed to create booking record");
    }
    
    console.log("✅ Booking record created successfully:");
    console.log("  - Booking ID:", booking.id);
    console.log("  - VB Number:", booking.vb_number);
    console.log("  - Status:", booking.status);
    console.log("  - Workflow Step:", booking.workflow_step);
    console.log("  - acquisition_source on row:", (booking as any).acquisition_source);
    console.log("  - visitor_id on row:", (booking as any).visitor_id);

    // Defensive verification — if the row came back without acquisition_source or
    // visitor_id (RLS/return shape edge cases), patch it now by id. The CRM badge
    // depends on these fields being populated.
    try {
      const needsPatch =
        (!(booking as any).acquisition_source && bookingAcquisition.source) ||
        (!(booking as any).visitor_id && bookingVisitorId);
      if (needsPatch) {
        console.warn('[EstimateForm] 🩹 Patching missing acquisition fields on booking', booking.id);
        await scopedClient
          .from('online_bookings')
          .update({
            visitor_id: bookingVisitorId,
            acquisition_source: bookingAcquisition.source,
            acquisition_medium: bookingAcquisition.medium,
            acquisition_campaign: bookingAcquisition.campaign,
            acquisition_term: bookingAcquisition.term,
            acquisition_entry_page: bookingAcquisition.entry_page,
            acquisition_referrer_domain: bookingAcquisition.referrer_domain,
          } as any)
          .eq('id', booking.id);
      }
    } catch (patchErr) {
      console.warn('[EstimateForm] Acquisition patch failed (non-fatal):', patchErr);
    }

    setVisitorBookingContext(booking.id, booking.vb_number, booking.customer_name);
    sessionStorage.setItem(`booking_vb_${booking.id}`, booking.vb_number);
    sessionStorage.setItem('current_vb_number', booking.vb_number);
    window.dispatchEvent(new CustomEvent('vb-number-set', { detail: { vb: booking.vb_number } }));

    // Defensive traffic attribution write — always run, even if usePageAnalytics
    // hasn't fired yet. captureAcquisition() ensures the session/visitor IDs and
    // the cached lt_acquisition exist synchronously.
    try {
      const { captureAcquisition, ensureIdentifiers } = await import('@/lib/attributionCapture');
      const acquisition = captureAcquisition();
      const { sessionId: analyticsSessionId, visitorId: analyticsVisitorId, isReturning } = ensureIdentifiers();
      const rawPageHistory = sessionStorage.getItem('visitor_page_history') || sessionStorage.getItem('analytics_page_history');
      const pageHistoryArr: string[] = rawPageHistory ? JSON.parse(rawPageHistory) : [acquisition.entry_page];

      const device = /Mobile|Android|iPhone/i.test(navigator.userAgent)
        ? 'mobile'
        : /Tablet|iPad/i.test(navigator.userAgent)
          ? 'tablet'
          : 'desktop';
      const browser = navigator.userAgent.includes('Chrome') && !navigator.userAgent.includes('Edg')
        ? 'Chrome'
        : navigator.userAgent.includes('Safari') && !navigator.userAgent.includes('Chrome')
          ? 'Safari'
          : navigator.userAgent.includes('Firefox')
            ? 'Firefox'
            : navigator.userAgent.includes('Edg')
              ? 'Edge'
              : 'Other';

      // Anonymous visitors have INSERT + UPDATE on traffic_sessions but no
      // SELECT, so we must never gate this on reading the row first — that
      // read always comes back empty for a real visitor and the conversion
      // silently never gets recorded. Update first, insert only if no row
      // was touched.
      const conversionFields = {
        vb_number: booking.vb_number,
        customer_name: booking.customer_name,
        workflow_step: 'quote_review',
        exit_page: window.location.pathname,
        page_history: pageHistoryArr,
        pages_viewed: Math.max(pageHistoryArr.length, 1),
        converted: true,
        conversion_type: 'quote_requested',
        engaged: true,
      };

      const { error: trackErr } = await supabase.rpc('track_session', {
        p_session_id: analyticsSessionId,
        p_data: {
          create: true,
          visitor_id: analyticsVisitorId,
          is_returning: isReturning,
          entry_page: acquisition.entry_page,
          source: acquisition.source,
          medium: acquisition.medium || (acquisition.source === 'referral' ? acquisition.referrer_domain : null),
          campaign: acquisition.campaign,
          term: acquisition.term,
          device_type: device,
          browser,
          ...conversionFields,
        } as any,
      });
      if (trackErr) console.error('[EstimateForm] Attribution write failed:', trackErr);
    } catch (attributionErr) {
      console.error('[EstimateForm] Attribution write failed (non-fatal):', attributionErr);
    }

    console.groupEnd();

    // Update toast
      toast({
        title: "Calculating Your Quote...",
        description: `Booking ${vbNumber} created. Generating instant quote...`,
      });

      // === STEP 3: Generate Quote via Edge Function ===
      console.group("🔵 STEP 3: Auto-Generate Quote");
      console.log("⏳ Calling auto-generate-quote edge function...");
      console.log("📤 Request Payload:", { bookingId: booking.id });
      
      const { data: quoteData, error: quoteError } = await supabase.functions.invoke('auto-generate-quote', {
        body: {
          bookingId: booking.id,
          openaiAdsChoice: await getOpenAIAdsChoice(),
        }
      });

    if (quoteError) {
      console.error("❌ Quote generation FAILED:");
      console.error("  - Message:", quoteError.message);
      console.error("  - Context:", quoteError.context);
      console.error("\n🔍 Full Error Object:", quoteError);
      
      // Check if it's a service area error
        const errorMessage = quoteError.message || '';
        const isServiceAreaError = errorMessage.includes('only provides pickup services') || 
                                   errorMessage.includes('out_of_service_area');
        
        if (isServiceAreaError) {
          console.warn("⚠️ Service area error detected - showing error to user");
          console.groupEnd();
          toast({
            title: "Outside Service Area",
            description: errorMessage.includes('Illinois, Indiana, Wisconsin, and Michigan') 
              ? errorMessage 
              : `We're sorry, but we only provide pickup services from Illinois, Indiana, Wisconsin, and Michigan. Please call us at (630) 686-9987 if you have questions.`,
            variant: "destructive",
            duration: 10000,
          });
          setIsSubmitting(false);
          submissionLockRef.current = false;
          return;
        }
        
        // Other errors - show partial success message
        console.warn("⚠️ Non-critical quote error - proceeding with partial success");
        console.groupEnd();
        toast({
          title: "Quote Request Received!",
          description: `Your booking reference is ${vbNumber}. We'll email your quote shortly.`,
          duration: 8000,
        });
        
        // Still redirect to review page for non-service-area errors
        setTimeout(() => {
          navigate(`/booking/${booking.id}/quote-review`);
        }, 2000);
      return;
    }

    // Success! Show confirmation and redirect
      console.log("✅ Quote generated successfully!");
      console.log("📊 Quote Data:", quoteData);
      console.groupEnd();
      
      toast({
        title: "Quote Generated Successfully!",
        description: `Your booking reference is ${vbNumber}. Redirecting to your quote...`,
        duration: 5000,
      });

      // Reset form
      setFormData({
        name: "",
        email: "",
        phone: "",
        fromZip: "",
        fromState: "",
        toZip: "",
        toState: "",
        moveDate: "",
        homeSize: "",
        notes: "",
        smsConsent: false,
      });

      // Mark as submitted to prevent abandonment tracking
      setIsSubmitted(true);

      // Redirect to quote review page
      setTimeout(() => {
        navigate(`/booking/${booking.id}/quote-review`);
      }, 1500);

    } catch (error) {
      console.group("🔴 FORM SUBMISSION ERROR (Caught in try/catch)");
      console.error("❌ Error Type:", error instanceof Error ? error.constructor.name : typeof error);
      console.error("❌ Error Message:", error instanceof Error ? error.message : String(error));
      console.error("\n🔍 Full Error Object:", error);
      console.error("\n📋 Context at time of error:");
      console.error("  - Form was submitting:", isSubmitting);
      console.error("  - Origin state:", formData.fromState);
      console.error("  - Destination state:", formData.toState);
      console.groupEnd();
      
      // Enhanced error logging with full context
      logFormSubmissionError("EstimateForm", error, {
        operation: "create",
        formData: {
          ...formData,
          email: '[REDACTED]',
          phone: '[REDACTED]',
          name: '[REDACTED]'
        },
        additionalContext: {
          isAuthenticated: !!(await supabase.auth.getSession()).data.session,
          userAgent: navigator.userAgent,
        }
      });
      
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to submit form. Please try again.",
        variant: "destructive",
      });
      setIsSubmitting(false);
      submissionLockRef.current = false;
    }
  };

  const goNext = () => {
    const errs: Record<string, string> = {};
    if (step === 1) {
      if (formData.name.trim().length < 2) errs.name = "Please enter your full name";
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) errs.email = "Please enter a valid email";
      if (formData.phone.replace(/\D/g, "").length < 10) errs.phone = "Please enter a valid phone number";
    } else if (step === 2) {
      if (!/^\d{5}$/.test(formData.fromZip)) errs.fromZip = "Please enter a 5-digit ZIP";
      if (!/^\d{5}$/.test(formData.toZip)) errs.toZip = "Please enter a 5-digit ZIP";
      if (originError) errs.fromZip = errs.fromZip || " ";
    }
    if (Object.keys(errs).length) {
      setValidationErrors({ ...validationErrors, ...errs });
      return;
    }
    setStep(step + 1);
  };

  return (
    <section id="estimate" className="py-12 bg-muted/30 notranslate" translate="no">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {/* Form Card */}
          <Card className="border-2 border-green-600 shadow-[var(--shadow-lg)]">
            <CardHeader className="text-center">
              <CardTitle className="text-2xl text-green-700">
                {multiStep ? "Instant Quote – Get Your Moving Cost on the Next Page" : "Instant Quote Form"}
              </CardTitle>
              <CardDescription className="text-center text-lg">
                Use this form for an instant, no-contact moving estimate. You may, if you choose, add inventory, validate your estimate, review insurance, sign your estimate, and reserve your move. Need help? Please give us a call (630) 686-9987.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Honeypot field - hidden from users, bots fill it */}
                <div 
                  style={{ 
                    position: 'absolute', 
                    left: '-9999px', 
                    opacity: 0, 
                    height: 0, 
                    overflow: 'hidden',
                    pointerEvents: 'none'
                  }}
                  aria-hidden="true"
                >
                  <label htmlFor="website">Website</label>
                  <input
                    type="text"
                    id="website"
                    name="website"
                    value={honeypot}
                    onChange={(e) => setHoneypot(e.target.value)}
                    tabIndex={-1}
                    autoComplete="off"
                  />
                </div>
                
                {multiStep && (
                  <div className="space-y-2" aria-live="polite">
                    <div className="flex justify-between text-sm font-medium text-muted-foreground">
                      <span>Step {step} of 3</span>
                      <span>{step === 1 ? "Your contact info" : step === 2 ? "Your move" : "Home details"}</span>
                    </div>
                    <div className="h-2 rounded-full bg-muted overflow-hidden">
                      <div className="h-full bg-secondary transition-all" style={{ width: `${(step / 3) * 100}%` }} />
                    </div>
                  </div>
                )}

                {/* Personal Info */}
                <div className={`grid grid-cols-1 md:grid-cols-2 gap-4 ${hide(1)}`}>
                  <div className="space-y-2">
                    <Label htmlFor="name">Full Name *</Label>
                    <Input
                      id="name"
                      placeholder="John Doe"
                      value={formData.name}
                      onChange={(e) => {
                        setFormData({ ...formData, name: e.target.value });
                        if (validationErrors.name) {
                          setValidationErrors({ ...validationErrors, name: "" });
                        }
                      }}
                      required
                      className={validationErrors.name ? "border-destructive" : ""}
                    />
                    {validationErrors.name && (
                      <p className="text-sm text-destructive">{validationErrors.name}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone Number *</Label>
                    <Input
                      id="phone"
                      type="tel"
                      placeholder="(630) 686-9987"
                      value={formData.phone}
                      onChange={(e) => {
                        setFormData({ ...formData, phone: formatPhoneNumber(e.target.value) });
                        if (validationErrors.phone) {
                          setValidationErrors({ ...validationErrors, phone: "" });
                        }
                      }}
                      required
                      className={validationErrors.phone ? "border-destructive" : ""}
                    />
                    {validationErrors.phone && (
                      <p className="text-sm text-destructive">{validationErrors.phone}</p>
                    )}
                  </div>
                </div>

                <div className={`space-y-2 ${hide(1)}`}>
                  <Label htmlFor="email">Email Address *</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="john@example.com"
                    value={formData.email}
                    onChange={(e) => {
                      setFormData({ ...formData, email: e.target.value });
                      if (validationErrors.email) {
                        setValidationErrors({ ...validationErrors, email: "" });
                      }
                    }}
                    required
                    className={validationErrors.email ? "border-destructive" : ""}
                  />
                  {validationErrors.email && (
                    <p className="text-sm text-destructive">{validationErrors.email}</p>
                  )}
                </div>

                {/* Move Details */}
                <div className={`grid grid-cols-1 md:grid-cols-2 gap-4 ${hide(2)}`}>
                  <div className="space-y-2">
                    <Label htmlFor="fromZip" className="flex items-center gap-2">
                      <HomeIcon className="h-4 w-4" />
                      From Zip Code *
                    </Label>
                    <Input
                      id="fromZip"
                      placeholder="60523"
                      value={formData.fromZip}
                      onChange={(e) => {
                        setFormData({ ...formData, fromZip: e.target.value });
                        if (validationErrors.fromZip) {
                          setValidationErrors({ ...validationErrors, fromZip: "" });
                        }
                      }}
                      required
                      maxLength={5}
                      className={validationErrors.fromZip || originError ? "border-destructive" : ""}
                    />
                    {validationErrors.fromZip && (
                      <p className="text-sm text-destructive">{validationErrors.fromZip}</p>
                    )}
                    {originError && (
                      <Alert variant="destructive" className="mt-2">
                        <AlertCircle className="h-4 w-4" />
                        <AlertDescription>
                          {originError}
                          <br />
                          <span className="text-sm mt-1 inline-block">
                            Call us at{" "}
                            <a href="tel:+16306869987" className="font-semibold underline">
                              (630) 686-9987
                            </a>{" "}
                            for assistance.
                          </span>
                        </AlertDescription>
                      </Alert>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="toZip" className="flex items-center gap-2">
                      <TruckIcon className="h-4 w-4" />
                      To Zip Code *
                    </Label>
                    <Input
                      id="toZip"
                      placeholder="90210"
                      value={formData.toZip}
                      onChange={(e) => {
                        setFormData({ ...formData, toZip: e.target.value });
                        if (validationErrors.toZip) {
                          setValidationErrors({ ...validationErrors, toZip: "" });
                        }
                      }}
                      required
                      className={validationErrors.toZip ? "border-destructive" : ""}
                    />
                    {validationErrors.toZip && (
                      <p className="text-sm text-destructive">{validationErrors.toZip}</p>
                    )}
                    {destinationError && (
                      <Alert className="mt-2">
                        <AlertCircle className="h-4 w-4" />
                        <AlertDescription>{destinationError}</AlertDescription>
                      </Alert>
                    )}
                  </div>
                </div>

                {/* Auto-detected State Confirmation */}
                {(formData.fromZip.length >= 5 || formData.toZip.length >= 5) && (
                  <div className={`grid grid-cols-1 md:grid-cols-2 gap-4 ${hide(2)}`}>
                    {formData.fromZip.length >= 5 && (
                      <div className="space-y-2">
                        <Label htmlFor="fromState">Origin State *</Label>
                        <Select
                          value={formData.fromState}
                          onValueChange={(value) => setFormData({ ...formData, fromState: value })}
                        >
                          <SelectTrigger id="fromState">
                            <SelectValue placeholder="Select state" />
                          </SelectTrigger>
                          <SelectContent>
                            {getAllStates()
                              .sort()
                              .map(state => (
                                <SelectItem key={state} value={state}>{state}</SelectItem>
                              ))}
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-muted-foreground">
                          Auto-detected from ZIP. Change if incorrect.
                        </p>
                      </div>
                    )}
                    {formData.toZip.length >= 5 && (
                      <div className="space-y-2">
                        <Label htmlFor="toState">
                          Destination State <span className="text-destructive">*</span>
                        </Label>
                        <Select
                          value={formData.toState}
                          onValueChange={(value) => setFormData({ ...formData, toState: value })}
                          required
                        >
                          <SelectTrigger 
                            id="toState"
                            className={validationErrors.toState ? "border-destructive" : ""}
                          >
                            <SelectValue placeholder="Select state" />
                          </SelectTrigger>
                          <SelectContent>
                            {getAllStates()
                              .sort()
                              .map(state => (
                                <SelectItem key={state} value={state}>{state}</SelectItem>
                              ))}
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-muted-foreground">
                          Auto-detected from ZIP. Change if incorrect.
                        </p>
                      </div>
                    )}
                  </div>
                )}

                <div className={multiStep ? "space-y-4" : "grid grid-cols-1 md:grid-cols-2 gap-4"}>
                  <div className={`space-y-2 ${hide(2)}`}>
                    <Label htmlFor="moveDate" className="flex items-center gap-2">
                      <CalendarIcon className="h-4 w-4" />
                      Preferred Move Date
                    </Label>
                    <Input
                      id="moveDate"
                      type="date"
                      min={new Date().toISOString().split('T')[0]}
                      value={formData.moveDate}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val) {
                          const today = new Date();
                          today.setHours(0, 0, 0, 0);
                          const picked = new Date(val + 'T00:00:00');
                          if (picked < today) {
                            toast({
                              title: "Invalid date",
                              description: "Move date cannot be in the past. Please pick today or a future date.",
                              variant: "destructive",
                            });
                            return;
                          }
                        }
                        setFormData({ ...formData, moveDate: val });
                      }}
                    />
                  </div>
                  <div className={`space-y-2 ${hide(3)}`}>
                    <Label htmlFor="homeSize">Home Size</Label>
                    <Select
                      value={formData.homeSize}
                      onValueChange={(value) => setFormData({ ...formData, homeSize: value })}
                    >
                      <SelectTrigger id="homeSize">
                        <SelectValue placeholder="Select home size" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="studio">Studio</SelectItem>
                        <SelectItem value="1br">1 Bedroom</SelectItem>
                        <SelectItem value="2br">2 Bedrooms</SelectItem>
                        <SelectItem value="3br">3 Bedrooms</SelectItem>
                        <SelectItem value="4br">4+ Bedrooms</SelectItem>
                        <SelectItem value="commercial">Commercial/Office</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className={`space-y-2 ${hide(3)}`}>
                  <Label htmlFor="notes">Additional Notes</Label>
                  <Textarea
                    id="notes"
                    placeholder="Notes, second stop at pick up, or delivery, special handling/packing, time constraints, emergency pick ups, COI needed, etc."
                    value={formData.notes}
                    onChange={(e) => {
                      setFormData({ ...formData, notes: e.target.value });
                      if (validationErrors.notes) {
                        setValidationErrors({ ...validationErrors, notes: "" });
                      }
                    }}
                    rows={4}
                    className={validationErrors.notes ? "border-destructive" : ""}
                  />
                  {validationErrors.notes && (
                    <p className="text-sm text-destructive">{validationErrors.notes}</p>
                  )}
                </div>

                {/* SMS Consent Checkbox */}
                <div className={`flex items-start gap-3 p-4 bg-muted/30 rounded-lg border ${hide(3)}`}>
                  <Checkbox
                    id="smsConsent"
                    checked={formData.smsConsent}
                    onCheckedChange={(checked) => 
                      setFormData({ ...formData, smsConsent: checked as boolean })
                    }
                    className="mt-1"
                  />
                  <div className="flex-1">
                    <label
                      htmlFor="smsConsent"
                      className="text-sm leading-relaxed cursor-pointer"
                    >
                      By clicking this box, you agree to receive SMS messages about customer care from LifeTime Movers. Reply STOP to opt out at any time. For help, text: +1 (630) 686-9987. Message data rates may apply. Messaging frequency may vary. See our{" "}
                      <a href="/privacy-policy" className="text-primary hover:underline">
                        Privacy Policy
                      </a>{" "}
                      and{" "}
                      <a href="/terms-and-conditions" className="text-primary hover:underline">
                        Terms and Conditions
                      </a>{" "}
                      here.
                    </label>
                  </div>
                </div>

                {multiStep && step < 3 ? (
                  <div className="flex gap-3">
                    {step > 1 && (
                      <Button type="button" variant="outline" size="lg" className="flex-1" onClick={() => setStep(step - 1)}>
                        Back
                      </Button>
                    )}
                    <Button type="button" size="lg" className="flex-1 bg-secondary hover:bg-secondary-light text-secondary-foreground" onClick={goNext}>
                      Next
                    </Button>
                  </div>
                ) : (
                <div className="flex gap-3">
                  {multiStep && (
                    <Button type="button" variant="outline" size="lg" onClick={() => setStep(2)}>
                      Back
                    </Button>
                  )}
                <Button 
                  type="submit" 
                  size="lg" 
                  className="flex-1 w-full bg-secondary hover:bg-secondary-light text-secondary-foreground" 
                  disabled={isSubmitting || !!originError || !formData.smsConsent}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Generating Your Quote...
                    </>
                  ) : (
                    "Get Instant Estimate"
                  )}
                </Button>
                </div>
                )}

                <p className="text-sm text-muted-foreground text-center">
                  * Required fields. We respect your privacy and will never share your information.
                </p>
              </form>

              {/* Duplicate Record Dialog */}
              <AlertDialog open={showDuplicateDialog} onOpenChange={setShowDuplicateDialog}>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Existing Quote Found</AlertDialogTitle>
                    <AlertDialogDescription asChild>
                      <div className="space-y-3">
                        <p>We found an existing record with your contact information:</p>
                        <div className="bg-muted p-3 rounded-md font-mono text-lg text-center">
                          {duplicateFound?.vbNumber}
                        </div>
                        <p className="text-sm text-muted-foreground">
                          Created {duplicateFound?.createdAt ? new Date(duplicateFound.createdAt).toLocaleDateString() : ''}
                        </p>
                        <p>Would you like to resume your existing booking or start fresh?</p>
                      </div>
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel onClick={() => {
                      setShowDuplicateDialog(false);
                      setDuplicateFound(null);
                      setBypassDuplicateCheck(true);
                      // Re-trigger form submission bypassing duplicate check
                      setTimeout(() => {
                        const form = document.querySelector('#estimate form') as HTMLFormElement;
                        if (form) form.requestSubmit();
                      }, 100);
                    }}>
                      Start Fresh Anyway
                    </AlertDialogCancel>
                    <AlertDialogAction onClick={() => {
                      navigate('/resume-booking');
                    }}>
                      Resume My Booking
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </CardContent>
          </Card>
        </div>
        </div>
      </section>
  );
};

export const EstimateForm = ({ multiStep = false }: { multiStep?: boolean }) => (
  <SentryErrorBoundary
    context="EstimateForm - Public Form"
    tags={{
      form_type: "estimate",
      section: "public",
      level: "component",
    }}
    level="component"
  >
    <EstimateFormInner multiStep={multiStep} />
  </SentryErrorBoundary>
);
